import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import Hero from './components/Hero';
import SalonConcept from './components/SalonConcept';
import Manifesto from './components/Manifesto';
import UpcomingSalon from './components/UpcomingSalon';
import ConsultingSection from './components/ConsultingSection';
import WorkshopSection from './components/WorkshopSection';
import ArchiveSection from './components/ArchiveSection';
import EmailCapture from './components/EmailCapture';
import ChatBot from './components/ChatBot';
import Footer from './components/Footer';
import InsightsMarquee from './components/InsightsMarquee';

type View = 'home' | 'manifesto' | 'practice' | 'workshop' | 'archive';

declare global {
  interface Window {
    gtag?: (command: string, action: string, params?: any) => void;
  }
}

const App: React.FC = () => {
  const [view, setView] = useState<View>('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleNavigate = (e: any) => {
      if (e.detail) navigateTo(e.detail as View);
    };
    window.addEventListener('navigate', handleNavigate);
    return () => window.removeEventListener('navigate', handleNavigate);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: view.charAt(0).toUpperCase() + view.slice(1),
        page_location: window.location.href,
        page_path: `/${view === 'home' ? '' : view}`,
      });
    }
  }, [view]);

  const navigateTo = (newView: View) => {
    setView(newView);
    setIsMenuOpen(false);
  };

  const navLinks = [
    { id: 'home', label: 'The Salon' },
    { id: 'archive', label: 'The Archive' },
    { id: 'manifesto', label: 'The Manifesto' },
    { id: 'practice', label: 'The Practice' },
    { id: 'workshop', label: 'The Workshop' },
  ];

  return (
    <div className="min-h-screen selection:bg-terracotta selection:text-white overflow-x-hidden bg-cream">
      <nav 
        className={`fixed top-0 left-0 right-0 z-[60] px-6 py-4 md:px-12 transition-all duration-300 ${
          scrolled || isMenuOpen ? 'bg-white/80 backdrop-blur-md shadow-sm border-b border-neutral-100' : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button 
            onClick={() => navigateTo('home')}
            className="text-xl font-black tracking-tighter text-charcoal cursor-pointer hover:text-terracotta transition-colors uppercase z-[70]"
          >
            V.A.I
          </button>

          <div className="hidden md:flex items-center gap-10 text-[10px] font-black uppercase tracking-[0.2em]">
            {navLinks.map((link) => (
              <button 
                key={link.id}
                onClick={() => navigateTo(link.id as View)} 
                className={`transition-colors font-black py-2 ${view === link.id ? 'text-terracotta' : 'text-neutral-400 hover:text-charcoal'}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          <button 
            className="md:hidden text-charcoal z-[70] p-2 hover:bg-neutral-100 rounded-full transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <div 
        className={`fixed inset-0 z-[55] bg-charcoal transition-all duration-500 ease-in-out md:hidden flex flex-col items-center justify-center gap-8 ${
          isMenuOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        {navLinks.map((link) => (
          <button 
            key={link.id}
            onClick={() => navigateTo(link.id as View)}
            className={`text-2xl font-black uppercase tracking-[0.3em] transition-colors ${
              view === link.id ? 'text-terracotta' : 'text-white hover:text-terracotta'
            }`}
          >
            {link.label}
          </button>
        ))}
      </div>

      <main className="animate-fade-in-up">
        {view === 'home' && (
          <>
            <Hero />
            <InsightsMarquee />
            <SalonConcept />
            <UpcomingSalon />
            <EmailCapture />
          </>
        )}
        
        {view === 'archive' && (
          <ArchiveSection onBack={() => navigateTo('home')} />
        )}

        {view === 'manifesto' && (
          <Manifesto 
            onNavigateToPractice={() => navigateTo('practice')} 
            onBack={() => navigateTo('home')}
          />
        )}

        {view === 'practice' && (
          <ConsultingSection onBack={() => navigateTo('home')} />
        )}

        {view === 'workshop' && (
          <WorkshopSection onBack={() => navigateTo('home')} />
        )}
      </main>

      <Footer />
      <ChatBot />
    </div>
  );
};

export default App;